import React, { useState, useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  TextField,
  MenuItem,
  Autocomplete,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Chip,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  Tabs,
  Tab,
  Box,
  InputAdornment,
} from "@mui/material";
import {
  ChevronDown,
  ChevronUp,
  Edit,
  Trash,
  Plus,
  PackageCheck,
  Save,
  X,
  Ticket,
} from "lucide-react";
import { dummyProducts, dummyVouchers } from "../../../../../dummyData";
import {
  errorToast,
  successToast,
} from "../../../../../services/toast.service";

// Sample order items data (would come from API in real app)
const sampleOrderItems = [
  {
    id: "item1",
    productId: "prod002",
    productName: "Anarkali Style Salwar Suit",
    size: "M",
    color: "Royal Blue",
    mrp: 3299,
    sellingPrice: 2699,
    quantity: 3,
    total: 8097,
    sellAsType: "primary",
  },
  {
    id: "item2",
    productId: "prod001",
    productName: "Chikankari Embroidered Salwar Suit",
    size: "Free Size",
    color: "Pink",
    mrp: 2499,
    sellingPrice: 1899,
    quantity: 5,
    total: 9495,
    sellAsType: "primary",
  },
  {
    id: "item3",
    productId: "prod003",
    productName: "Designer Palazzo Suit",
    size: "L",
    color: "Mint Green",
    mrp: 1999,
    sellingPrice: 1499,
    quantity: 4,
    total: 5996,
    sellAsType: "primary",
  },
];

// Sample sell as options for each product
const sampleSellAsOptions = {
  primary: [{ label: "Individual Unit", value: "primary" }],
  secondary: [
    { label: "Standard Pack (8 pieces)", value: "standard_pack" },
    { label: "Display Pack (2 pieces)", value: "display_pack" },
  ],
  tertiary: [{ label: "Carton Pack (40 pieces)", value: "carton_pack" }],
};

const OrderItemsTable = ({
  orderId,
  onOrderItemsUpdated,
  isCreateOrder,
  isApproved,
}) => {
  const [expanded, setExpanded] = useState(true);
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [editMode, setEditMode] = useState(false);
  const [editedItems, setEditedItems] = useState([]);
  const [productDialogOpen, setProductDialogOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedSellAsType, setSelectedSellAsType] = useState("primary");
  const [selectedSellAsOption, setSelectedSellAsOption] = useState("");
  const [selectedTabIndex, setSelectedTabIndex] = useState(0);
  const [newItemData, setNewItemData] = useState({
    quantity: 1,
    size: "",
    color: "",
  });

  // Voucher state
  const [appliedVouchers, setAppliedVouchers] = useState([]);
  const [selectedVoucher, setSelectedVoucher] = useState(null);
  const [voucherError, setVoucherError] = useState("");
  const [discount, setDiscount] = useState(0);

  // Fetch order items (simulated)
  useEffect(() => {
    setLoading(true);
    // In a real app, this would be an API call using the orderId
    setTimeout(() => {
      // If isCreateOrder is true, start with an empty list
      if (isCreateOrder) {
        setItems([]);
        setEditedItems([]);
        // If it's a create order, auto-enable edit mode
        setEditMode(true);
      } else {
        setItems(sampleOrderItems);
        setEditedItems(sampleOrderItems.map(item => ({ ...item })));
      }
      setLoading(false);
    }, 500);
  }, [orderId, isCreateOrder]);

  // Handle item field edit
  const handleEditItem = (id, field, value) => {
    const updatedItems = editedItems.map(item => {
      if (item.id === id) {
        const updatedItem = { ...item, [field]: value };

        // Recalculate total if quantity or sellingPrice changes
        if (field === "quantity" || field === "sellingPrice") {
          updatedItem.total = updatedItem.quantity * updatedItem.sellingPrice;
        }

        return updatedItem;
      }
      return item;
    });

    setEditedItems(updatedItems);
  };

  // Handle item deletion
  const handleDeleteItem = id => {
    setEditedItems(editedItems.filter(item => item.id !== id));
  };

  // Calculate totals
  const subtotal = editedItems.reduce((sum, item) => sum + item.total, 0);
  const tax = Math.round(subtotal * 0.18); // 18% GST
  const grandTotal = subtotal - discount + tax;

  // Handle applying voucher
  const handleApplyVoucher = () => {
    if (!selectedVoucher) {
      errorToast("Please select a voucher");
      return;
    }

    // Check if voucher already applied
    if (appliedVouchers.some(v => v.code === selectedVoucher.code)) {
      errorToast("This voucher has already been applied");
      return;
    }

    // Check minimum order value requirement
    if (selectedVoucher.minOrderValue > subtotal) {
      errorToast(
        `This voucher requires a minimum order of ₹${selectedVoucher.minOrderValue}`,
      );
      return;
    }

    // Add voucher to applied vouchers
    setAppliedVouchers([...appliedVouchers, selectedVoucher]);
    setSelectedVoucher(null);
    setVoucherError("");

    successToast(`Voucher ${selectedVoucher.code} applied successfully`);

    // Calculate new discount
    calculateDiscount([...appliedVouchers, selectedVoucher]);
  };

  // Remove a voucher
  const handleRemoveVoucher = voucherCode => {
    const updatedVouchers = appliedVouchers.filter(v => v.code !== voucherCode);
    setAppliedVouchers(updatedVouchers);
    calculateDiscount(updatedVouchers);

    successToast(`Voucher removed successfully`);
  };

  // Calculate discount from applied vouchers
  const calculateDiscount = vouchers => {
    let newDiscount = 0;

    // Calculate discount from vouchers
    vouchers.forEach(voucher => {
      if (voucher.type === "percentage") {
        const percentageDiscount = subtotal * (voucher.percentage / 100);
        // Apply max discount cap if specified
        const voucherDiscount =
          voucher.maxDiscount > 0
            ? Math.min(percentageDiscount, voucher.maxDiscount)
            : percentageDiscount;

        newDiscount += voucherDiscount;
      } else {
        newDiscount += voucher.amount;
      }
    });

    // Ensure discount doesn't exceed subtotal
    newDiscount = Math.min(newDiscount, subtotal);
    setDiscount(newDiscount);

    // Call callback with updated totals
    if (onOrderItemsUpdated) {
      onOrderItemsUpdated({
        subtotal: subtotal,
        discount: newDiscount,
        tax: tax,
        grandTotal: subtotal - newDiscount + tax,
      });
    }
  };

  // Save changes
  const handleSaveChanges = () => {
    setItems([...editedItems]);
    setEditMode(false);

    // Calculate new totals
    const newSubtotal = editedItems.reduce((sum, item) => sum + item.total, 0);
    const newTax = Math.round(newSubtotal * 0.18); // 18% GST
    const newGrandTotal = newSubtotal - discount + newTax;

    // Call the callback with updated totals if provided
    if (onOrderItemsUpdated) {
      onOrderItemsUpdated({
        subtotal: newSubtotal,
        discount: discount,
        tax: newTax,
        grandTotal: newGrandTotal,
      });
    }
  };

  // Cancel changes
  const handleCancelEdit = () => {
    setEditedItems(items.map(item => ({ ...item })));
    setEditMode(false);
  };

  // Open Add Product Dialog
  const handleAddProductClick = () => {
    setProductDialogOpen(true);
    setSelectedProduct(null);
    setSelectedSellAsType("primary");
    setSelectedSellAsOption("");
    setNewItemData({
      quantity: 1,
      size: "",
      color: "",
    });
    setSelectedTabIndex(0);
  };

  // Close Product Dialog
  const handleCloseProductDialog = () => {
    setProductDialogOpen(false);
  };

  // Handle product selection
  const handleProductSelection = product => {
    setSelectedProduct(product);
    if (product) {
      setNewItemData({
        ...newItemData,
        size: product.size?.split(", ")[0] || "",
        color: product.color || "",
      });
    } else {
      // Handle clearing the selection
      setSelectedProduct(null);
      setNewItemData({
        quantity: 1,
        size: "",
        color: "",
      });
    }
  };

  // Handle tab change in product dialog
  const handleTabChange = (event, newValue) => {
    setSelectedTabIndex(newValue);
  };

  // Add new item to order
  const handleAddItem = () => {
    if (!selectedProduct) return;

    // Calculate quantity and price based on sell as type
    let finalQuantity = newItemData.quantity;
    let packSize = 1;

    if (selectedSellAsType === "secondary") {
      packSize = 8; // Standard secondary pack has 8 items
      if (selectedSellAsOption === "display_pack") {
        packSize = 2; // Display pack has 2 items
      }
    } else if (selectedSellAsType === "tertiary") {
      packSize = 40; // Carton pack has 40 items
    }

    // Create new item
    const newItem = {
      id: `item${Date.now()}`,
      productId: selectedProduct._id,
      productName: selectedProduct.name,
      size:
        selectedSellAsType === "primary"
          ? newItemData.size
          : selectedSellAsType === "secondary"
          ? "M, L, XL" // For secondary packs, show all sizes
          : "Standard Pack", // For tertiary packs
      color: newItemData.color,
      mrp: selectedProduct.mrp,
      sellingPrice: selectedProduct.sellingPrice,
      quantity: finalQuantity,
      packSize: packSize,
      totalItems: finalQuantity * packSize,
      total: selectedProduct.sellingPrice * finalQuantity * packSize,
      sellAsType: selectedSellAsType,
    };

    setEditedItems([...editedItems, newItem]);
    setProductDialogOpen(false);
  };

  // Render sell as options for the selected product
  const renderSellAsOptions = () => {
    if (!selectedProduct) return null;

    return (
      <div className="mb-6">
        <h3 className="text-sm font-medium text-gray-700 mb-3">Sell As</h3>
        <FormControl component="fieldset">
          <RadioGroup
            value={selectedSellAsType}
            onChange={e => setSelectedSellAsType(e.target.value)}>
            <FormControlLabel
              value="primary"
              control={
                <Radio
                  sx={{
                    color: "#5652B0",
                    "&.Mui-checked": { color: "#5652B0" },
                  }}
                />
              }
              label="Individual Unit"
            />
            <FormControlLabel
              value="secondary"
              control={
                <Radio
                  sx={{
                    color: "#5652B0",
                    "&.Mui-checked": { color: "#5652B0" },
                  }}
                />
              }
              label="Secondary Pack"
            />
            <FormControlLabel
              value="tertiary"
              control={
                <Radio
                  sx={{
                    color: "#5652B0",
                    "&.Mui-checked": { color: "#5652B0" },
                  }}
                />
              }
              label="Tertiary Pack"
            />
          </RadioGroup>
        </FormControl>

        {selectedSellAsType !== "primary" && (
          <div className="mt-3 ml-8">
            <FormControl fullWidth>
              <label className="text-sm text-gray-600 mb-1">Pack Type</label>
              <TextField
                select
                size="small"
                value={selectedSellAsOption}
                onChange={e => setSelectedSellAsOption(e.target.value)}
                fullWidth>
                {sampleSellAsOptions[selectedSellAsType].map(option => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            </FormControl>
          </div>
        )}
      </div>
    );
  };

  // Render dialog tabs
  const renderDialogTabs = () => {
    return (
      <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
        <Tabs
          value={selectedTabIndex}
          onChange={handleTabChange}
          sx={{
            "& .MuiTab-root": {
              textTransform: "none",
              fontSize: "0.875rem",
              fontFamily: "Outfit",
            },
            "& .Mui-selected": {
              color: "#5652B0",
            },
            "& .MuiTabs-indicator": {
              backgroundColor: "#5652B0",
            },
          }}>
          <Tab label="Product Details" />
          <Tab label="Pack Configuration" />
          <Tab label="Pricing" />
        </Tabs>
      </Box>
    );
  };

  // Render Product Details Tab
  const renderProductDetailsTab = () => {
    if (!selectedProduct) return null;

    return (
      <div className="space-y-4">
        <div className="bg-[#F9FAFB] p-3 rounded-md border border-gray-200">
          <p className="text-sm font-medium text-gray-700 mb-2">
            Product Details
          </p>
          <div className="space-y-2">
            <p className="text-sm flex justify-between">
              <span className="text-gray-600">SKU:</span>
              <span className="font-medium text-gray-800">
                {selectedProduct.productId}
              </span>
            </p>
            <p className="text-sm flex justify-between">
              <span className="text-gray-600">Material:</span>
              <span className="font-medium text-gray-800">
                {selectedProduct.material}
              </span>
            </p>
            <p className="text-sm flex justify-between">
              <span className="text-gray-600">Primary Unit:</span>
              <span className="font-medium text-gray-800">
                {selectedProduct.soldAs}
              </span>
            </p>
            <p className="text-sm flex justify-between items-center">
              <span className="text-gray-600">Color:</span>
              <span className="flex items-center gap-1.5">
                <span className="w-4 h-4 bg-[#C4A484] rounded-full border border-gray-200"></span>
                <span className="font-medium text-gray-800">
                  {selectedProduct.color}
                </span>
              </span>
            </p>
          </div>
        </div>

        {renderSellAsOptions()}
      </div>
    );
  };

  // Render Pack Configuration Tab
  const renderPackConfigTab = () => {
    if (!selectedProduct) return null;

    return (
      <div className="bg-white rounded-md py-2">
        <h3 className="text-sm font-medium text-gray-700 mb-3">
          Pack Configuration
        </h3>

        {selectedSellAsType === "primary" ? (
          <div className="text-sm text-gray-600 italic">
            Not applicable for individual units
          </div>
        ) : (
          <div className="bg-[#F9FAFB] p-3 rounded-md border border-gray-200">
            <TableContainer
              component={Paper}
              elevation={0}
              className="border border-gray-200 rounded-lg">
              <Table size="small">
                <TableHead className="bg-[#F9F8FE]">
                  <TableRow>
                    <TableCell className="font-medium text-gray-700">
                      Size
                    </TableCell>
                    <TableCell className="font-medium text-gray-700">
                      Color
                    </TableCell>
                    <TableCell
                      align="center"
                      className="font-medium text-gray-700">
                      Quantity
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {selectedSellAsType === "secondary" ? (
                    <>
                      <TableRow>
                        <TableCell>M</TableCell>
                        <TableCell>{selectedProduct.color}</TableCell>
                        <TableCell align="center">3</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>L</TableCell>
                        <TableCell>{selectedProduct.color}</TableCell>
                        <TableCell align="center">3</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>XL</TableCell>
                        <TableCell>{selectedProduct.color}</TableCell>
                        <TableCell align="center">2</TableCell>
                      </TableRow>
                    </>
                  ) : (
                    <>
                      <TableRow>
                        <TableCell>Standard Pack</TableCell>
                        <TableCell>{selectedProduct.color}</TableCell>
                        <TableCell align="center">5</TableCell>
                      </TableRow>
                    </>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </div>
        )}
      </div>
    );
  };

  // Render Pricing Tab
  const renderPricingTab = () => {
    if (!selectedProduct) return null;

    return (
      <div className="space-y-4">
        <div className="bg-[#F9FAFB] p-3 rounded-md border border-gray-200">
          <p className="text-sm font-medium text-gray-700 mb-2">Pricing</p>
          <div className="space-y-2">
            <p className="text-sm flex justify-between">
              <span className="text-gray-600">MRP:</span>
              <span className="text-gray-400 line-through">
                ₹{selectedProduct.mrp.toFixed(2)}
              </span>
            </p>
            <p className="text-sm flex justify-between">
              <span className="text-gray-600">Selling Price:</span>
              <span className="font-semibold text-[#5652B0]">
                ₹{selectedProduct.sellingPrice.toFixed(2)}
              </span>
            </p>
            <p className="text-sm flex justify-between">
              <span className="text-gray-600">Pack Total (If applicable):</span>
              <span className="font-medium text-gray-800">
                {selectedSellAsType === "secondary"
                  ? `₹${(selectedProduct.sellingPrice * 8).toFixed(2)}`
                  : selectedSellAsType === "tertiary"
                  ? `₹${(selectedProduct.sellingPrice * 40).toFixed(2)}`
                  : "—"}
              </span>
            </p>
          </div>
        </div>

        <div className="space-y-3">
          <div className="flex items-end gap-4">
            <TextField
              label="Quantity"
              type="number"
              size="small"
              value={newItemData.quantity}
              onChange={e =>
                setNewItemData({
                  ...newItemData,
                  quantity: parseInt(e.target.value) || 1,
                })
              }
              inputProps={{ min: 1 }}
              sx={{ width: "100px" }}
            />

            <TextField
              label="Size"
              select
              size="small"
              value={newItemData.size}
              onChange={e =>
                setNewItemData({ ...newItemData, size: e.target.value })
              }
              sx={{ width: "100px" }}>
              {selectedProduct.size.split(", ").map(size => (
                <MenuItem key={size} value={size}>
                  {size}
                </MenuItem>
              ))}
            </TextField>

            <div className="flex-1"></div>

            <div className="text-right">
              <div className="text-sm text-gray-600">Item Total:</div>
              <div className="text-lg font-semibold text-[#5652B0]">
                ₹
                {(selectedProduct.sellingPrice * newItemData.quantity).toFixed(
                  2,
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Add Product Dialog
  const renderAddProductDialog = () => {
    return (
      <Dialog
        open={productDialogOpen}
        onClose={handleCloseProductDialog}
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: {
            height: "80vh",
            maxHeight: "700px",
            display: "flex",
            flexDirection: "column",
          },
        }}>
        <DialogTitle
          sx={{
            p: 3,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            borderBottom: "1px solid",
            borderColor: "divider",
            bgcolor: "#F9F8FE",
          }}>
          <span className="text-xl font-semibold text-gray-800">
            Add Product to Order
          </span>
          <IconButton onClick={handleCloseProductDialog} edge="end">
            <X size={20} />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers sx={{ p: 0, flex: 1, overflow: "auto" }}>
          <div className="flex flex-col md:flex-row h-full">
            {/* Left side: Search & Product Selection */}
            <div className="w-full md:w-1/3 border-r border-gray-200 bg-white p-4 overflow-auto">
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Search Products
                </label>
                <Autocomplete
                  options={dummyProducts}
                  getOptionLabel={option => option?.name || ""}
                  filterOptions={(options, state) => {
                    const inputValue = state.inputValue.toLowerCase().trim();
                    if (!inputValue) return options;

                    return options.filter(
                      option =>
                        option.name.toLowerCase().includes(inputValue) ||
                        option.brand.name.toLowerCase().includes(inputValue) ||
                        option.productId.toLowerCase().includes(inputValue),
                    );
                  }}
                  renderInput={params => (
                    <TextField
                      {...params}
                      placeholder="Search by name, brand or code..."
                      fullWidth
                      variant="outlined"
                      size="small"
                    />
                  )}
                  onChange={(event, newValue) => {
                    handleProductSelection(newValue);
                  }}
                  isOptionEqualToValue={(option, value) =>
                    option?._id === value?._id
                  }
                  renderOption={(props, option) => (
                    <li
                      {...props}
                      className="py-2 px-3 hover:bg-[#F9F8FE] border-b border-gray-100 last:border-0">
                      <div className="flex items-start gap-2">
                        <div className="w-8 h-8 bg-[#F9F8FE] rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                          <PackageCheck size={15} className="text-[#5652B0]" />
                        </div>
                        <div className="flex flex-col">
                          <div className="font-medium text-gray-800">
                            {option.name}
                          </div>
                          <div className="text-xs text-gray-500 mt-0.5 flex items-center gap-1.5">
                            <span className="font-medium bg-gray-100 px-1.5 py-0.5 rounded">
                              {option.productId}
                            </span>
                            <span>{option.brand.name}</span>
                            <span className="text-[#5652B0] font-medium ml-auto">
                              ₹{option.sellingPrice}
                            </span>
                          </div>
                        </div>
                      </div>
                    </li>
                  )}
                  ListboxProps={{
                    style: {
                      maxHeight: "350px",
                    },
                  }}
                  PopperProps={{
                    style: {
                      width: "fit-content",
                      minWidth: "100%",
                      maxWidth: "450px",
                    },
                    placement: "bottom-start",
                  }}
                  noOptionsText={
                    <div className="py-2 px-3 text-sm text-gray-500 text-center">
                      No products found
                    </div>
                  }
                />
              </div>

              {selectedProduct && (
                <div className="mt-4 bg-[#F9FAFB] p-3 rounded-md border border-gray-200">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-8 h-8 bg-[#F9F8FE] rounded-full flex items-center justify-center">
                      <PackageCheck size={16} className="text-[#5652B0]" />
                    </div>
                    <h3 className="font-medium text-gray-800">
                      {selectedProduct.name}
                    </h3>
                  </div>

                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">SKU:</span>
                      <span className="font-medium">
                        {selectedProduct.productId}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Brand:</span>
                      <span className="font-medium">
                        {selectedProduct.brand.name}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">MRP:</span>
                      <span className="text-gray-500 line-through">
                        ₹{selectedProduct.mrp}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Selling Price:</span>
                      <span className="font-medium text-[#5652B0]">
                        ₹{selectedProduct.sellingPrice}
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Right side: Configuration options */}
            <div className="w-full md:w-2/3 p-4 overflow-auto">
              {!selectedProduct ? (
                <div className="flex flex-col items-center justify-center h-64 text-gray-500">
                  <PackageCheck size={48} className="mb-3 text-gray-300" />
                  <p>Please select a product to continue</p>
                </div>
              ) : (
                <>
                  <h3 className="text-lg font-medium text-gray-800 mb-4">
                    Add to Order
                  </h3>

                  {/* Sell As Options */}
                  <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      How would you like to sell?
                    </label>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      <div
                        className={`border rounded-lg p-3 cursor-pointer transition-colors
                          ${
                            selectedSellAsType === "primary"
                              ? "border-[#5652B0] bg-[#F9F8FE]"
                              : "border-gray-200 hover:bg-gray-50"
                          }`}
                        onClick={() => setSelectedSellAsType("primary")}>
                        <div className="flex items-center mb-1">
                          <Radio
                            checked={selectedSellAsType === "primary"}
                            sx={{
                              padding: 0,
                              marginRight: 1,
                              color: "#5652B0",
                              "&.Mui-checked": { color: "#5652B0" },
                            }}
                          />
                          <span className="font-medium">Individual Unit</span>
                        </div>
                        <p className="text-xs text-gray-500 ml-5">
                          Sell as single items
                        </p>
                      </div>

                      <div
                        className={`border rounded-lg p-3 cursor-pointer transition-colors
                          ${
                            selectedSellAsType === "secondary"
                              ? "border-[#5652B0] bg-[#F9F8FE]"
                              : "border-gray-200 hover:bg-gray-50"
                          }`}
                        onClick={() => setSelectedSellAsType("secondary")}>
                        <div className="flex items-center mb-1">
                          <Radio
                            checked={selectedSellAsType === "secondary"}
                            sx={{
                              padding: 0,
                              marginRight: 1,
                              color: "#5652B0",
                              "&.Mui-checked": { color: "#5652B0" },
                            }}
                          />
                          <span className="font-medium">Secondary Pack</span>
                        </div>
                        <p className="text-xs text-gray-500 ml-5">
                          Multiple units in a pack
                        </p>
                      </div>

                      <div
                        className={`border rounded-lg p-3 cursor-pointer transition-colors
                          ${
                            selectedSellAsType === "tertiary"
                              ? "border-[#5652B0] bg-[#F9F8FE]"
                              : "border-gray-200 hover:bg-gray-50"
                          }`}
                        onClick={() => setSelectedSellAsType("tertiary")}>
                        <div className="flex items-center mb-1">
                          <Radio
                            checked={selectedSellAsType === "tertiary"}
                            sx={{
                              padding: 0,
                              marginRight: 1,
                              color: "#5652B0",
                              "&.Mui-checked": { color: "#5652B0" },
                            }}
                          />
                          <span className="font-medium">Tertiary Pack</span>
                        </div>
                        <p className="text-xs text-gray-500 ml-5">
                          Bulk carton packaging
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Pack Type Selection (if applicable) */}
                  {selectedSellAsType !== "primary" && (
                    <div className="mb-6">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Select Pack Type
                      </label>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {sampleSellAsOptions[selectedSellAsType].map(option => (
                          <div
                            key={option.value}
                            className={`border rounded-lg p-3 cursor-pointer transition-colors
                              ${
                                selectedSellAsOption === option.value
                                  ? "border-[#5652B0] bg-[#F9F8FE]"
                                  : "border-gray-200 hover:bg-gray-50"
                              }`}
                            onClick={() =>
                              setSelectedSellAsOption(option.value)
                            }>
                            <div className="flex items-center mb-1">
                              <Radio
                                checked={selectedSellAsOption === option.value}
                                sx={{
                                  padding: 0,
                                  marginRight: 1,
                                  color: "#5652B0",
                                  "&.Mui-checked": { color: "#5652B0" },
                                }}
                              />
                              <span className="font-medium">
                                {option.label}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Pack Configuration (if applicable) */}
                  {selectedSellAsType !== "primary" && selectedSellAsOption && (
                    <div className="mb-6">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Pack Configuration
                      </label>
                      <div className="bg-[#F9FAFB] border border-gray-200 rounded-lg overflow-hidden">
                        <TableContainer component={Paper} elevation={0}>
                          <Table size="small">
                            <TableHead className="bg-[#F9F8FE]">
                              <TableRow>
                                <TableCell className="font-medium text-gray-700 py-2">
                                  Size
                                </TableCell>
                                <TableCell className="font-medium text-gray-700 py-2">
                                  Color
                                </TableCell>
                                <TableCell
                                  align="center"
                                  className="font-medium text-gray-700 py-2">
                                  Quantity
                                </TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {selectedSellAsType === "secondary" ? (
                                selectedSellAsOption === "display_pack" ? (
                                  <>
                                    <TableRow>
                                      <TableCell className="py-2">M</TableCell>
                                      <TableCell className="py-2">
                                        {selectedProduct.color}
                                      </TableCell>
                                      <TableCell
                                        align="center"
                                        className="py-2">
                                        1
                                      </TableCell>
                                    </TableRow>
                                    <TableRow>
                                      <TableCell className="py-2">L</TableCell>
                                      <TableCell className="py-2">
                                        {selectedProduct.color}
                                      </TableCell>
                                      <TableCell
                                        align="center"
                                        className="py-2">
                                        1
                                      </TableCell>
                                    </TableRow>
                                  </>
                                ) : (
                                  <>
                                    <TableRow>
                                      <TableCell className="py-2">M</TableCell>
                                      <TableCell className="py-2">
                                        {selectedProduct.color}
                                      </TableCell>
                                      <TableCell
                                        align="center"
                                        className="py-2">
                                        3
                                      </TableCell>
                                    </TableRow>
                                    <TableRow>
                                      <TableCell className="py-2">L</TableCell>
                                      <TableCell className="py-2">
                                        {selectedProduct.color}
                                      </TableCell>
                                      <TableCell
                                        align="center"
                                        className="py-2">
                                        3
                                      </TableCell>
                                    </TableRow>
                                    <TableRow>
                                      <TableCell className="py-2">XL</TableCell>
                                      <TableCell className="py-2">
                                        {selectedProduct.color}
                                      </TableCell>
                                      <TableCell
                                        align="center"
                                        className="py-2">
                                        2
                                      </TableCell>
                                    </TableRow>
                                  </>
                                )
                              ) : (
                                <>
                                  <TableRow>
                                    <TableCell className="py-2">
                                      Standard Pack
                                    </TableCell>
                                    <TableCell className="py-2">
                                      {selectedProduct.color}
                                    </TableCell>
                                    <TableCell align="center" className="py-2">
                                      5
                                    </TableCell>
                                  </TableRow>
                                  <TableRow>
                                    <TableCell className="py-2">
                                      Display Pack
                                    </TableCell>
                                    <TableCell className="py-2">
                                      {selectedProduct.color}
                                    </TableCell>
                                    <TableCell align="center" className="py-2">
                                      3
                                    </TableCell>
                                  </TableRow>
                                </>
                              )}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </div>
                    </div>
                  )}

                  {/* Order Details */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                    {selectedSellAsType === "primary" && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Size
                        </label>
                        <TextField
                          select
                          fullWidth
                          size="small"
                          value={newItemData.size}
                          onChange={e =>
                            setNewItemData({
                              ...newItemData,
                              size: e.target.value,
                            })
                          }>
                          {selectedProduct?.size?.split(", ").map(size => (
                            <MenuItem key={size} value={size}>
                              {size}
                            </MenuItem>
                          ))}
                        </TextField>
                      </div>
                    )}

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {selectedSellAsType === "primary"
                          ? "Quantity"
                          : `Number of ${
                              selectedSellAsType === "secondary"
                                ? "Packs"
                                : "Cartons"
                            }`}
                      </label>
                      <TextField
                        fullWidth
                        type="number"
                        size="small"
                        value={newItemData.quantity}
                        onChange={e =>
                          setNewItemData({
                            ...newItemData,
                            quantity: parseInt(e.target.value) || 1,
                          })
                        }
                        inputProps={{ min: 1 }}
                      />
                    </div>
                  </div>

                  {/* Price summary section in add product modal - Right Side */}
                  <div className="mt-6 bg-gray-50 p-4 rounded-lg border border-gray-200">
                    <h3 className="text-sm font-medium text-gray-700 mb-3">
                      Price Summary
                    </h3>

                    <div className="space-y-2">
                      {selectedProduct && (
                        <>
                          {selectedSellAsType === "primary" ? (
                            <>
                              <div className="flex justify-between text-sm">
                                <span className="text-gray-600">
                                  Unit Price:
                                </span>
                                <span className="font-medium">
                                  ₹
                                  {selectedProduct.sellingPrice.toLocaleString()}
                                </span>
                              </div>
                              <div className="flex justify-between text-sm">
                                <span className="text-gray-600">Quantity:</span>
                                <span className="font-medium">
                                  {newItemData.quantity}
                                </span>
                              </div>
                            </>
                          ) : (
                            <>
                              <div className="flex justify-between text-sm">
                                <span className="text-gray-600">
                                  Pack Price:
                                </span>
                                <span className="font-medium">
                                  ₹
                                  {selectedSellAsType === "secondary"
                                    ? (
                                        selectedProduct.sellingPrice *
                                        (selectedSellAsOption === "display_pack"
                                          ? 2
                                          : 8)
                                      ).toLocaleString()
                                    : (
                                        selectedProduct.sellingPrice * 40
                                      ).toLocaleString()}
                                </span>
                              </div>
                              <div className="flex justify-between text-sm">
                                <span className="text-gray-600">
                                  Number of Packs:
                                </span>
                                <span className="font-medium">
                                  {newItemData.quantity}
                                </span>
                              </div>
                              <div className="flex justify-between text-sm">
                                <span className="text-gray-600">
                                  Items per Pack:
                                </span>
                                <span className="font-medium">
                                  {selectedSellAsType === "secondary"
                                    ? selectedSellAsOption === "display_pack"
                                      ? 2
                                      : 8
                                    : 40}
                                </span>
                              </div>
                              <div className="flex justify-between text-sm">
                                <span className="text-gray-600">
                                  Total Items:
                                </span>
                                <span className="font-medium">
                                  {newItemData.quantity *
                                    (selectedSellAsType === "secondary"
                                      ? selectedSellAsOption === "display_pack"
                                        ? 2
                                        : 8
                                      : 40)}
                                </span>
                              </div>
                            </>
                          )}

                          <div className="flex justify-between text-sm pt-2 mt-1 border-t border-gray-200">
                            <span className="font-medium text-gray-700">
                              Subtotal:
                            </span>
                            <span className="font-semibold text-[#5652B0]">
                              ₹
                              {(
                                selectedProduct.sellingPrice *
                                newItemData.quantity *
                                (selectedSellAsType === "primary"
                                  ? 1
                                  : selectedSellAsType === "secondary"
                                  ? selectedSellAsOption === "display_pack"
                                    ? 2
                                    : 8
                                  : 40)
                              ).toLocaleString()}
                            </span>
                          </div>

                          <div className="flex justify-between text-sm">
                            <span className="text-gray-600">
                              Tax (18% GST):
                            </span>
                            <span className="font-medium">
                              ₹
                              {Math.round(
                                selectedProduct.sellingPrice *
                                  newItemData.quantity *
                                  (selectedSellAsType === "primary"
                                    ? 1
                                    : selectedSellAsType === "secondary"
                                    ? selectedSellAsOption === "display_pack"
                                      ? 2
                                      : 8
                                    : 40) *
                                  0.18,
                              ).toLocaleString()}
                            </span>
                          </div>

                          <div className="flex justify-between text-sm pt-2 mt-1 border-t border-gray-200">
                            <span className="font-medium text-gray-700">
                              Total:
                            </span>
                            <span className="font-semibold text-[#5652B0]">
                              ₹
                              {Math.round(
                                selectedProduct.sellingPrice *
                                  newItemData.quantity *
                                  (selectedSellAsType === "primary"
                                    ? 1
                                    : selectedSellAsType === "secondary"
                                    ? selectedSellAsOption === "display_pack"
                                      ? 2
                                      : 8
                                    : 40) *
                                  1.18,
                              ).toLocaleString()}
                            </span>
                          </div>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Vouchers Section - Show in all screens but with different UI based on mode */}
                  <div className="mt-6">
                    <div className="flex justify-between items-center mb-3">
                      <h3 className="text-sm font-medium text-gray-700">
                        {isCreateOrder ? "Add Vouchers" : "Redeemed Vouchers"}
                      </h3>

                      {isCreateOrder && (
                        <button
                          onClick={() => {
                            setSelectedTabIndex(2); // Switch to voucher tab
                          }}
                          className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-[#5652B0] border border-[#5652B0] rounded-md hover:bg-[#F9F8FF]">
                          <Plus size={12} />
                          <span>Add Voucher</span>
                        </button>
                      )}
                    </div>

                    {isCreateOrder ? (
                      <div className="flex justify-between items-center mb-3">
                        <div className="w-full">
                          <div className="flex items-center gap-3 mb-3">
                            <Autocomplete
                              id="voucher-select"
                              options={dummyVouchers}
                              value={selectedVoucher}
                              getOptionLabel={option => option?.code || ""}
                              renderInput={params => (
                                <TextField
                                  {...params}
                                  placeholder="Enter voucher code"
                                  size="small"
                                  className="flex-1"
                                  InputProps={{
                                    ...params.InputProps,
                                    startAdornment: (
                                      <>
                                        <InputAdornment position="start">
                                          <Ticket
                                            size={16}
                                            className="text-[#5652B0]"
                                          />
                                        </InputAdornment>
                                        {params.InputProps.startAdornment}
                                      </>
                                    ),
                                  }}
                                  sx={{
                                    width: "100%",
                                    minWidth: "300px",
                                    "& .MuiOutlinedInput-root": {
                                      padding: "2px 14px",
                                    },
                                  }}
                                />
                              )}
                              onChange={(event, newValue) => {
                                setSelectedVoucher(newValue);
                                if (voucherError) setVoucherError("");
                              }}
                              isOptionEqualToValue={(option, value) =>
                                option?.id === value?.id
                              }
                              renderOption={(props, option) => (
                                <li
                                  {...props}
                                  className="py-3 px-3 hover:bg-[#F9F8FE] border-b border-gray-100 last:border-0">
                                  <div className="w-full">
                                    <div className="flex justify-between">
                                      <span className="font-medium text-gray-800">
                                        {option.code}
                                      </span>
                                      <span className="text-[#5652B0] font-medium">
                                        {option.type === "fixed"
                                          ? `₹${option.amount}`
                                          : `${option.percentage}%`}{" "}
                                        off
                                      </span>
                                    </div>
                                    <div className="text-sm text-gray-500 mt-1">
                                      <span>{option.description}</span>
                                    </div>
                                    <div className="text-xs text-gray-400 mt-1 flex justify-between">
                                      <span>
                                        {option.minOrderValue > 0
                                          ? `Min order: ₹${option.minOrderValue}`
                                          : "No minimum order"}
                                      </span>
                                      <span>
                                        Expires:{" "}
                                        {new Date(
                                          option.expiryDate,
                                        ).toLocaleDateString()}
                                      </span>
                                    </div>
                                  </div>
                                </li>
                              )}
                              ListboxProps={{
                                style: { maxHeight: "300px" },
                              }}
                              PaperProps={{
                                style: {
                                  maxWidth: "400px",
                                  boxShadow: "0 4px 20px rgba(0, 0, 0, 0.1)",
                                },
                              }}
                              fullWidth
                            />
                            <button
                              onClick={handleApplyVoucher}
                              disabled={!selectedVoucher}
                              className={`flex-shrink-0 flex items-center gap-1.5 px-6 py-2.5 rounded-lg text-base font-medium ${
                                !selectedVoucher
                                  ? "bg-gray-400 text-white cursor-not-allowed"
                                  : "text-white bg-[#5652B0] hover:bg-[#4A47A3] transition-all"
                              }`}>
                              <Plus size={14} />
                              <span>Apply</span>
                            </button>
                          </div>

                          {appliedVouchers.length > 0 ? (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-4">
                              {appliedVouchers.map(voucher => (
                                <div
                                  key={voucher.id}
                                  className="bg-[#F9F8FE] border border-[#EFEDFB] rounded-md p-3 flex items-start justify-between">
                                  <div className="flex items-start gap-2">
                                    <Ticket
                                      size={16}
                                      className="text-[#5652B0] mt-0.5"
                                    />
                                    <div>
                                      <div className="text-sm font-medium text-gray-800">
                                        {voucher.code}
                                      </div>
                                      <div className="text-xs text-gray-500">
                                        {voucher.description}
                                      </div>
                                      <div className="text-xs text-[#5652B0] mt-0.5 font-medium">
                                        {voucher.type === "fixed"
                                          ? `₹${voucher.amount} off`
                                          : `${voucher.percentage}% off`}
                                      </div>
                                    </div>
                                  </div>
                                  <button
                                    onClick={() =>
                                      handleRemoveVoucher(voucher.code)
                                    }
                                    className="text-gray-400 hover:text-red-500">
                                    <X size={14} />
                                  </button>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div className="bg-[#F9FAFB] border border-gray-200 rounded-lg p-4 min-h-[80px] flex flex-col items-center justify-center">
                              <p className="text-sm text-gray-500 mb-1">
                                No vouchers applied yet
                              </p>
                              <p className="text-xs text-[#4B5563]">
                                Search and select a voucher to get discounts
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                    ) : (
                      <div className="grid grid-cols-2 gap-2">
                        <div className="bg-[#F9F8FE] border border-[#EFEDFB] rounded-md p-2.5 flex items-start gap-2">
                          <Ticket size={16} className="text-[#5652B0] mt-0.5" />
                          <div>
                            <div className="text-sm font-medium text-gray-800">
                              FIRST50
                            </div>
                            <div className="text-xs text-gray-500">
                              ₹500 off on your first order
                            </div>
                          </div>
                        </div>
                        <div className="bg-[#F9F8FE] border border-[#EFEDFB] rounded-md p-2.5 flex items-start gap-2">
                          <Ticket size={16} className="text-[#5652B0] mt-0.5" />
                          <div>
                            <div className="text-sm font-medium text-gray-800">
                              SUMMER23
                            </div>
                            <div className="text-xs text-gray-500">
                              ₹200 off on orders above ₹2000
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </DialogContent>

        <DialogActions
          sx={{ p: 3, bgcolor: "#FAFAFA", borderTop: "1px solid #E5E7EB" }}>
          <Button
            onClick={handleCloseProductDialog}
            variant="outlined"
            sx={{
              textTransform: "none",
              borderColor: "rgba(0, 0, 0, 0.23)",
              color: "rgba(0, 0, 0, 0.87)",
            }}>
            Cancel
          </Button>
          <Button
            onClick={handleAddItem}
            variant="contained"
            disabled={
              !selectedProduct ||
              (selectedSellAsType !== "primary" && !selectedSellAsOption)
            }
            startIcon={<Plus size={16} />}
            sx={{
              textTransform: "none",
              bgcolor: "#5652B0",
              "&:hover": {
                bgcolor: "#4A47A3",
              },
            }}>
            Add to Order
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
      <div
        className="flex items-center justify-between px-6 py-4 border-b cursor-pointer"
        onClick={() => setExpanded(!expanded)}>
        <h2 className="text-lg font-semibold text-gray-800">Order Items</h2>
        <div className="flex items-center">
          <span className="mr-4 text-sm font-medium text-gray-500">
            {editedItems.length} items
          </span>
          <button className="p-1 rounded-full hover:bg-gray-100">
            {expanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
          </button>
        </div>
      </div>

      {expanded && (
        <div className="p-6">
          {loading ? (
            <div className="flex justify-center py-8">
              <CircularProgress size={40} />
            </div>
          ) : (
            <>
              <div className="flex justify-between mb-4">
                <div className="flex gap-2">
                  {editMode && !isCreateOrder ? (
                    <>
                      <button
                        onClick={handleSaveChanges}
                        className="flex items-center gap-1.5 px-3 py-1.5 bg-[#5652B0] text-white rounded-md">
                        <Save size={16} />
                        <span className="text-sm">Save Changes</span>
                      </button>
                      <button
                        onClick={handleCancelEdit}
                        className="flex items-center gap-1.5 px-3 py-1.5 border border-gray-300 text-gray-700 rounded-md">
                        <X size={16} />
                        <span className="text-sm">Cancel</span>
                      </button>
                    </>
                  ) : (
                    !isCreateOrder &&
                    !isApproved && (
                      <button
                        onClick={() => setEditMode(true)}
                        className="flex items-center gap-1.5 px-3 py-1.5 border border-[#5652B0] text-[#5652B0] rounded-md hover:bg-[#F9F8FF] transition-colors">
                        <Edit size={16} />
                        <span className="text-sm">Edit Items</span>
                      </button>
                    )
                  )}
                </div>

                {(editMode || isCreateOrder) && (
                  <button
                    onClick={handleAddProductClick}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-[#F9F8FE] text-[#5652B0] border border-[#EFEDFB] rounded-md">
                    <Plus size={16} />
                    <span className="text-sm">Add Product</span>
                  </button>
                )}
              </div>

              {editedItems.length === 0 && isCreateOrder ? (
                <div className="flex flex-col items-center justify-center py-12 text-gray-500 bg-gray-50 rounded-lg border border-dashed border-gray-300">
                  <PackageCheck size={48} className="mb-3 text-gray-300" />
                  <p className="mb-1">No items added to this order yet</p>
                  <p className="text-sm">
                    Click "Add Product" to add items to this order
                  </p>
                </div>
              ) : (
                <TableContainer
                  component={Paper}
                  elevation={0}
                  className="border border-gray-200 rounded-lg mb-6">
                  <Table size="small">
                    <TableHead className="bg-[#F9F8FE]">
                      <TableRow>
                        <TableCell className="font-medium text-gray-700 py-2">
                          Product Name
                        </TableCell>
                        <TableCell className="font-medium text-gray-700 py-2">
                          Size
                        </TableCell>
                        <TableCell className="font-medium text-gray-700 py-2">
                          Color
                        </TableCell>
                        <TableCell className="font-medium text-gray-700 py-2">
                          MRP
                        </TableCell>
                        <TableCell className="font-medium text-gray-700 py-2">
                          Selling Price
                        </TableCell>
                        <TableCell className="font-medium text-gray-700 py-2">
                          Qty (Packs)
                        </TableCell>
                        <TableCell className="font-medium text-gray-700 py-2">
                          Total Items
                        </TableCell>
                        <TableCell
                          align="right"
                          className="font-medium text-gray-700 py-2">
                          Total
                        </TableCell>
                        {editMode && (
                          <TableCell width="60px" className="py-2"></TableCell>
                        )}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {editedItems.map(item => (
                        <TableRow key={item.id} hover>
                          <TableCell
                            component="th"
                            scope="row"
                            className="font-medium py-2 h-[48px]">
                            {item.productName}
                            {item.sellAsType !== "primary" && (
                              <Chip
                                label={
                                  item.sellAsType === "secondary"
                                    ? "Secondary Pack"
                                    : "Tertiary Pack"
                                }
                                size="small"
                                sx={{
                                  ml: 1,
                                  backgroundColor: "#F9F8FE",
                                  color: "#5652B0",
                                  border: "1px solid #EFEDFB",
                                  height: "18px",
                                  "& .MuiChip-label": {
                                    px: 1,
                                    fontSize: "0.6875rem",
                                  },
                                }}
                              />
                            )}
                          </TableCell>
                          <TableCell className="py-2 h-[48px]">
                            {editMode ? (
                              <TextField
                                value={item.size}
                                onChange={e =>
                                  handleEditItem(
                                    item.id,
                                    "size",
                                    e.target.value,
                                  )
                                }
                                variant="outlined"
                                size="small"
                                sx={{
                                  width:
                                    item.sellAsType !== "primary"
                                      ? "140px"
                                      : "80px",
                                  "& .MuiInputBase-root": {
                                    height: "32px",
                                  },
                                }}
                              />
                            ) : (
                              item.size
                            )}
                          </TableCell>
                          <TableCell className="py-2 h-[48px]">
                            {editMode ? (
                              <TextField
                                value={item.color}
                                onChange={e =>
                                  handleEditItem(
                                    item.id,
                                    "color",
                                    e.target.value,
                                  )
                                }
                                variant="outlined"
                                size="small"
                                sx={{
                                  width: "100px",
                                  "& .MuiInputBase-root": {
                                    height: "32px",
                                  },
                                }}
                              />
                            ) : (
                              item.color
                            )}
                          </TableCell>
                          <TableCell className="py-2 h-[48px]">
                            ₹{item.mrp.toLocaleString()}
                          </TableCell>
                          <TableCell className="py-2 h-[48px]">
                            {editMode ? (
                              <TextField
                                value={item.sellingPrice}
                                onChange={e =>
                                  handleEditItem(
                                    item.id,
                                    "sellingPrice",
                                    parseInt(e.target.value) || 0,
                                  )
                                }
                                variant="outlined"
                                size="small"
                                type="number"
                                sx={{
                                  width: "100px",
                                  "& .MuiInputBase-root": {
                                    height: "32px",
                                  },
                                }}
                              />
                            ) : (
                              `₹${item.sellingPrice.toLocaleString()}`
                            )}
                          </TableCell>
                          <TableCell className="py-2 h-[48px]">
                            {editMode ? (
                              <TextField
                                value={item.quantity}
                                onChange={e =>
                                  handleEditItem(
                                    item.id,
                                    "quantity",
                                    parseInt(e.target.value) || 0,
                                  )
                                }
                                variant="outlined"
                                size="small"
                                type="number"
                                inputProps={{ min: 1 }}
                                sx={{
                                  width: "80px",
                                  "& .MuiInputBase-root": {
                                    height: "32px",
                                  },
                                }}
                              />
                            ) : (
                              item.quantity
                            )}
                          </TableCell>
                          <TableCell className="py-2 h-[48px]">
                            {item.totalItems ||
                              item.quantity * (item.packSize || 1)}
                          </TableCell>
                          <TableCell align="right" className="py-2 h-[48px]">
                            ₹{item.total.toLocaleString()}
                          </TableCell>
                          {editMode && (
                            <TableCell className="py-2 h-[48px]">
                              <IconButton
                                size="small"
                                onClick={() => handleDeleteItem(item.id)}
                                color="error">
                                <Trash size={15} />
                              </IconButton>
                            </TableCell>
                          )}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}

              <div className="flex justify-between items-start">
                {/* Redeemed Vouchers - Left Side */}
                <div className="flex-1 max-w-xl">
                  <h3 className="text-sm font-medium text-gray-700 mb-3">
                    {isCreateOrder ? "Add Vouchers" : "Redeemed Vouchers"}
                  </h3>

                  {isCreateOrder ? (
                    <div className="flex justify-between items-center mb-3">
                      <div className="w-full">
                        <div className="flex items-center gap-3 mb-3">
                          <Autocomplete
                            id="voucher-select"
                            options={dummyVouchers}
                            value={selectedVoucher}
                            getOptionLabel={option => option?.code || ""}
                            renderInput={params => (
                              <TextField
                                {...params}
                                placeholder="Enter voucher code"
                                size="small"
                                className="flex-1"
                                InputProps={{
                                  ...params.InputProps,
                                  startAdornment: (
                                    <>
                                      <InputAdornment position="start">
                                        <Ticket
                                          size={16}
                                          className="text-[#5652B0]"
                                        />
                                      </InputAdornment>
                                      {params.InputProps.startAdornment}
                                    </>
                                  ),
                                }}
                                sx={{
                                  width: "100%",
                                  minWidth: "300px",
                                  "& .MuiOutlinedInput-root": {
                                    padding: "2px 14px",
                                  },
                                }}
                              />
                            )}
                            onChange={(event, newValue) => {
                              setSelectedVoucher(newValue);
                              if (voucherError) setVoucherError("");
                            }}
                            isOptionEqualToValue={(option, value) =>
                              option?.id === value?.id
                            }
                            renderOption={(props, option) => (
                              <li
                                {...props}
                                className="py-3 px-3 hover:bg-[#F9F8FE] border-b border-gray-100 last:border-0">
                                <div className="w-full">
                                  <div className="flex justify-between">
                                    <span className="font-medium text-gray-800">
                                      {option.code}
                                    </span>
                                    <span className="text-[#5652B0] font-medium">
                                      {option.type === "fixed"
                                        ? `₹${option.amount}`
                                        : `${option.percentage}%`}{" "}
                                      off
                                    </span>
                                  </div>
                                  <div className="text-sm text-gray-500 mt-1">
                                    <span>{option.description}</span>
                                  </div>
                                  <div className="text-xs text-gray-400 mt-1 flex justify-between">
                                    <span>
                                      {option.minOrderValue > 0
                                        ? `Min order: ₹${option.minOrderValue}`
                                        : "No minimum order"}
                                    </span>
                                    <span>
                                      Expires:{" "}
                                      {new Date(
                                        option.expiryDate,
                                      ).toLocaleDateString()}
                                    </span>
                                  </div>
                                </div>
                              </li>
                            )}
                            ListboxProps={{
                              style: { maxHeight: "300px" },
                            }}
                            PaperProps={{
                              style: {
                                maxWidth: "400px",
                                boxShadow: "0 4px 20px rgba(0, 0, 0, 0.1)",
                              },
                            }}
                            fullWidth
                          />
                          <button
                            onClick={handleApplyVoucher}
                            disabled={!selectedVoucher}
                            className={`flex-shrink-0 flex items-center gap-1.5 px-6 py-2.5 rounded-lg text-base font-medium ${
                              !selectedVoucher
                                ? "bg-gray-400 text-white cursor-not-allowed"
                                : "text-white bg-[#5652B0] hover:bg-[#4A47A3] transition-all"
                            }`}>
                            <Plus size={14} />
                            <span>Apply</span>
                          </button>
                        </div>

                        {appliedVouchers.length > 0 ? (
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-4">
                            {appliedVouchers.map(voucher => (
                              <div
                                key={voucher.id}
                                className="bg-[#F9F8FE] border border-[#EFEDFB] rounded-md p-3 flex items-start justify-between">
                                <div className="flex items-start gap-2">
                                  <Ticket
                                    size={16}
                                    className="text-[#5652B0] mt-0.5"
                                  />
                                  <div>
                                    <div className="text-sm font-medium text-gray-800">
                                      {voucher.code}
                                    </div>
                                    <div className="text-xs text-gray-500">
                                      {voucher.description}
                                    </div>
                                    <div className="text-xs text-[#5652B0] mt-0.5 font-medium">
                                      {voucher.type === "fixed"
                                        ? `₹${voucher.amount} off`
                                        : `${voucher.percentage}% off`}
                                    </div>
                                  </div>
                                </div>
                                <button
                                  onClick={() =>
                                    handleRemoveVoucher(voucher.code)
                                  }
                                  className="text-gray-400 hover:text-red-500">
                                  <X size={14} />
                                </button>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="bg-[#F9FAFB] border border-gray-200 rounded-lg p-4 min-h-[80px] flex flex-col items-center justify-center">
                            <p className="text-sm text-gray-500 mb-1">
                              No vouchers applied yet
                            </p>
                            <p className="text-xs text-[#4B5563]">
                              Search and select a voucher to get discounts
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 gap-2">
                      <div className="bg-[#F9F8FE] border border-[#EFEDFB] rounded-md p-2.5 flex items-start gap-2">
                        <Ticket size={16} className="text-[#5652B0] mt-0.5" />
                        <div>
                          <div className="text-sm font-medium text-gray-800">
                            FIRST50
                          </div>
                          <div className="text-xs text-gray-500">
                            ₹500 off on your first order
                          </div>
                        </div>
                      </div>
                      <div className="bg-[#F9F8FE] border border-[#EFEDFB] rounded-md p-2.5 flex items-start gap-2">
                        <Ticket size={16} className="text-[#5652B0] mt-0.5" />
                        <div>
                          <div className="text-sm font-medium text-gray-800">
                            SUMMER23
                          </div>
                          <div className="text-xs text-gray-500">
                            ₹200 off on orders above ₹2000
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Price Summary - Right Side */}
                <div className="w-80">
                  <div className="space-y-1.5">
                    <div className="flex justify-between text-gray-600">
                      <span>Subtotal</span>
                      <span className="font-medium">
                        ₹{subtotal.toLocaleString()}
                      </span>
                    </div>
                    <div className="flex justify-between text-gray-600">
                      <span>Discount</span>
                      <span className="font-medium text-green-600">
                        {isCreateOrder
                          ? discount > 0
                            ? `-₹${discount.toLocaleString()}`
                            : "₹0"
                          : "-₹500"}
                      </span>
                    </div>
                    <div className="flex justify-between text-gray-600">
                      <span>Tax (18% GST)</span>
                      <span className="font-medium">
                        ₹{tax.toLocaleString()}
                      </span>
                    </div>
                    <div className="flex justify-between pt-2 mt-1.5 border-t border-gray-200">
                      <span className="font-medium text-gray-800">
                        Grand Total
                      </span>
                      <span className="font-semibold text-[#5652B0]">
                        ₹{grandTotal.toLocaleString()}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {/* Add Product Dialog */}
      {renderAddProductDialog()}
    </div>
  );
};

export default OrderItemsTable;
